<script setup>
import TheCustomerMetaNavigation from '@/components/basic/atoms/TheCustomerMetaNavigation.vue'
import UspBar from '@/components/basic/atoms/UspBar.vue'
import { translate } from '@/plugins/i18n'
import {
  containsPrivateInfo,
  INDEX_FOLLOW,
  NO_INDEX_FOLLOW
} from '@/services/helpers/MetaHelper.js'
import { useHead } from '@unhead/vue'
import { computed, defineAsyncComponent, onMounted, ref } from 'vue'
import { useDisplay, useGoTo } from 'vuetify'

const TheHeader = defineAsyncComponent(
  () => import('@/components/basic/organisms/TheHeader.vue')
)
const AppBanner = defineAsyncComponent(
  () => import('@/components/basic/molecules/AppBanner.vue')
)
const TheFooter = defineAsyncComponent(
  () => import('@/components/basic/organisms/TheFooter.vue')
)
const LazyComponent = defineAsyncComponent(
  () => import('@/components/basic/atoms/LazyComponent.vue')
)

const title = translate('page_title_winery_a_z')
const description = translate('page_description_winery_a_z')

const manufacturers = ref(null)
const searchQuery = ref('')
const goTo = useGoTo()

const hasManufacturers = computed(() => {
  return (
    manufacturers.value &&
    Array.isArray(manufacturers.value) &&
    manufacturers.value.length > 0
  )
})

const isLoading = computed(() => {
  return manufacturers.value === null
})

const clusteredManufacturers = computed(() => {
  if (!manufacturers.value || !Array.isArray(manufacturers.value)) {
    return {}
  }

  const normalizeChar = (char) => {
    return char
      .normalize('NFD')
      .replaceAll(/[\u0300-\u036f]/g, '')
      .replaceAll(/[äÄ]/g, 'A')
      .replaceAll(/[öÖ]/g, 'O')
      .replaceAll(/[üÜ]/g, 'U')
      .toUpperCase()
  }

  const clusters = {}
  const query = searchQuery.value ? searchQuery.value.toLowerCase().trim() : ''

  manufacturers.value.forEach((manufacturer) => {
    const name = manufacturer.name.trim()
    if (!name) return

    if (query && !name.toLowerCase().includes(query)) {
      return
    }

    const firstChar = normalizeChar(name.charAt(0))

    let clusterKey
    if (/[0-9A-Z]/.test(firstChar)) {
      clusterKey = firstChar
    } else {
      clusterKey = '#'
    }

    if (!clusters[clusterKey]) {
      clusters[clusterKey] = []
    }

    clusters[clusterKey].push(manufacturer)
  })

  const sortedClusters = {}
  const sortedKeys = Object.keys(clusters).sort((a, b) => {
    const aIsNumber = /\d/.test(a)
    const bIsNumber = /\d/.test(b)

    if (aIsNumber && !bIsNumber) return -1
    if (!aIsNumber && bIsNumber) return 1
    if (aIsNumber && bIsNumber)
      return a.localeCompare(b, undefined, { numeric: true })
    return a.localeCompare(b)
  })

  sortedKeys.forEach((key) => {
    sortedClusters[key] = clusters[key].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    )
  })

  return sortedClusters
})

const { mdAndUp } = useDisplay()

const headerHeight = computed(() => {
  return mdAndUp.value ? document.querySelector('header').clientHeight : 0
})

const scrollToLetter = (letter) => {
  const elementId = `letter-${letter}`
  goTo(`#${elementId}`, {
    offset: -headerHeight.value
  })
}

onMounted(async () => {
  const { default: ApiController } = await import('@/services/ApiController')
  manufacturers.value = await ApiController.fetchAllManufacturers()
})

useHead({
  title,
  meta: [
    {
      name: 'robots',
      content: computed(() =>
        containsPrivateInfo() ? NO_INDEX_FOLLOW : INDEX_FOLLOW
      )
    },
    { name: 'description', content: description }
  ]
})
</script>

<template>
  <div id="content" itemscope itemtype="https://schema.org/manufacturer">
    <AppBanner />
    <UspBar />
    <TheCustomerMetaNavigation />
    <TheHeader />
    <VContainer id="all-manufacturers-container">
      <VRow class="justify-center">
        <VCol cols="12" class="d-flex flex-column align-center">
          <h1 class="text-center my-5 serif c-winered font-weight-bold">
            {{ title }}
          </h1>
          <VBtn
            v-if="isLoading"
            class="mt-5"
            color="white"
            icon
            rounded="50%"
            width="56"
            height="56"
            loading
          >
            <VIcon>custom:reset</VIcon>
          </VBtn>
        </VCol>

        <VCol
          v-if="hasManufacturers"
          cols="12"
          md="10"
          lg="8"
          class="px-0 mt-5"
        >
          <VCard class="pa-5" elevation="0">
            <VTextField
              v-model="searchQuery"
              :label="translate('manufacturers.search_placeholder')"
              prepend-inner-icon="custom:search"
              clearable
              class="mt-5"
            />

            <div class="mt-5 d-flex justify-between flex-wrap text-center">
              <VBtn
                variant="text"
                v-for="letter in Object.keys(clusteredManufacturers)"
                :key="letter"
                @click="scrollToLetter(letter)"
                class="pa-0"
                min-width="56"
              >
                {{ letter }}
              </VBtn>
            </div>
          </VCard>
        </VCol>
      </VRow>
      <VRow
        v-if="hasManufacturers"
        v-for="(cluster, letter) in clusteredManufacturers"
        :key="letter"
        class="mt-5 align-self-start text-left"
      >
        <VCol cols="12">
          <h2
            :id="`letter-${letter}`"
            class="serif font-size-xxxxxl c-winered font-weight-bold mt-5"
          >
            {{ letter }}
          </h2>
          <div class="mt-3">
            <VRow>
              <VCol
                v-for="(manufacturer, manufacturerIndex) in cluster"
                :key="manufacturerIndex"
                cols="12"
                sm="6"
                md="4"
              >
                <div :key="manufacturer.manufacturer_id">
                  <RouterLink
                    :to="`/${manufacturer.full_url}`"
                    class="link-graphit"
                  >
                    {{ manufacturer.name }}
                  </RouterLink>
                </div>
              </VCol>
            </VRow>
          </div>
        </VCol>
      </VRow>
    </VContainer>
    <LazyComponent id="footer" :delay="1500">
      <TheFooter />
    </LazyComponent>
  </div>
</template>

<style scoped>
#all-manufacturers-container {
  min-height: 100vh;
}
</style>
